import { FC } from "react";

import { FormType, getCurrencyFromCountry } from "@simplyk/common";

import { CampaignDescription } from "../../campaign-details/CampaignDescription/CampaignDescription";
import { CampaignOrgChip } from "../../campaign-details/CampaignOrgChip/CampaignOrgChip";
import { OrganizationHostedBy } from "../../peer-to-peer/OrganizationHostedBy/OrganizationHostedBy";

import { CampaignBanner } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignBanner/CampaignBanner";
import { CampaignTargetThermometer } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignTargetThermometer/CampaignTargetThermometer";
import { AuctionSummaryLayout } from "@/canvas/features/FormV2/blocks/payment/Summary/AuctionSummaryLayout";
import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { AuctionV2CoreInfo } from "@/canvas/features/FormV2/steps/step1/shared/CoreInfo/AuctionV2CoreInfo";
import { trpc } from "@/common/helpers/trpc";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import { FormCampaignTargetThermometerSkeleton } from "@/forms-design-system/FormCampaignTargetThermometer";
import { FormDivider } from "@/forms-design-system/FormDivider";

export const AuctionSummary: FC = () => {
  const { formObject, description, formOrganization, bannerAspectRatio, hasBanner } = useFormV2Context(
    FormType.Ticketing
  );
  const isCampaign = !!formObject.thermometerTarget;
  const isOneColumnLayout = !hasBanner && !description && !isCampaign;

  const { isSmallScreen } = useMediaQuery();
  const { isPreview } = usePreviewContext();
  const isThermometerGhostPreview = useIsGhostPreview("FormV2Thermometer");
  const isDescriptionGhostPreview = useIsGhostPreview("FormV2Description");

  const { data: campaignVolumeData, isLoading: isCampaignLoading } = trpc.form_getStoredFormVolume.useQuery(
    {
      formId: formObject.id,
      formType: FormType.Ticketing,
    },
    {
      enabled: isCampaign && !isPreview,
    }
  );

  const currency = getCurrencyFromCountry(formOrganization.country);

  const banner = formObject.bannerUrl
    ? { imageSrc: formObject.bannerUrl, imageAlt: formObject.bannerAltText }
    : formObject.bannerVideoUrl
      ? { videoSrc: formObject.bannerVideoUrl }
      : undefined;

  const campaign = isCampaign
    ? {
        isLoading: isCampaignLoading && !isPreview,
        currentCollectedAmount: isPreview ? (formObject?.thermometerTarget ?? 0) * 0.3 : (campaignVolumeData ?? 0),
        targetedAmount: formObject.thermometerTarget ?? 0,
        currency,
      }
    : undefined;

  return (
    <AuctionSummaryLayout
      slots={{
        banner: banner && <CampaignBanner {...banner} aspectRatio={bannerAspectRatio} />,
        organizationLogo: isSmallScreen ? (
          <CampaignOrgChip />
        ) : (
          <OrganizationHostedBy logoSrc={formObject.logoUrl} organizationName={formObject.organization?.name} />
        ),
        coreInfo: <AuctionV2CoreInfo />,
        campaignTarget: campaign ? (
          campaign.isLoading ? (
            <FormCampaignTargetThermometerSkeleton />
          ) : (
            <CampaignTargetThermometer
              currentCollectedAmount={campaign.currentCollectedAmount}
              targetedAmount={campaign.targetedAmount}
              currency={campaign.currency}
              sx={{
                opacity: isThermometerGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
              }}
            />
          )
        ) : undefined,
        description: description && (
          <CampaignDescription
            sx={{
              opacity: isDescriptionGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
            }}
          />
        ),
        divider: !isOneColumnLayout ? <FormDivider sx={{ width: "100%" }} /> : undefined,
      }}
    />
  );
};
